import { Loader2, Building2, X } from 'lucide-react'
import { useEffect } from 'react'

interface CompaniesHouseAuthModalProps {
  isOpen: boolean
  onClose: () => void
  authUrl: string | null
  isRequestingAuth: boolean
  onAuthorize: () => void
  companyNumber: string
}

export default function CompaniesHouseAuthModal({
  isOpen,
  onClose,
  authUrl,
  isRequestingAuth,
  onAuthorize,
  companyNumber,
}: CompaniesHouseAuthModalProps) {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleAuthorize = () => {
    if (authUrl) {
      // Store destination route for after auth completion
      sessionStorage.setItem('companiesHouseAuthRedirect', '/companies-house-filing')
      sessionStorage.setItem('companiesHouseCompanyNumber', companyNumber)
      window.location.href = authUrl
    } else {
      onAuthorize()
    }
  }

  return (
    <div
      className='fixed inset-0 flex items-center justify-center z-50 p-4'
      style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role='presentation'
    >
      <div
        className='bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-auto relative'
        onClick={event => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
        tabIndex={-1}
      >
        {/* Header */}
        <div className='px-8 pt-8 pb-6 border-b border-gray-100'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
                <Building2 className='w-6 h-6 text-blue-600' />
              </div>
              <div>
                <h2 className='text-2xl font-bold text-gray-900'>
                  Companies House Authorization
                </h2>
                <p className='text-gray-500 text-sm mt-1'>
                  Connect to Companies House filing system
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors'
            >
              <X className='w-5 h-5 text-gray-600' />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='px-8 py-6'>
          <div className='mb-6'>
            <p className='text-gray-700 text-base leading-relaxed'>
              To file documents with Companies House, you need to authorize this application
              to access the Companies House filing system on behalf of your company.
            </p>
            {companyNumber && (
              <div className='mt-3 p-3 bg-gray-50 rounded-lg'>
                <p className='text-sm text-gray-600'>
                  <strong className='font-semibold'>Company Number:</strong> {companyNumber}
                </p>
              </div>
            )}
          </div>

          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
            <p className='text-sm text-blue-900'>
              <strong className='font-semibold'>Authorization Process:</strong>
            </p>
            <ul className='list-disc list-inside text-sm text-blue-800 mt-2 space-y-1'>
              <li>You&apos;ll be redirected to Companies House</li>
              <li>Sign in with your Companies House account</li>
              <li>Grant filing permissions for your company</li>
              <li>You&apos;ll be redirected back to complete your filing</li>
            </ul>
          </div>

          <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6'>
            <p className='text-sm text-amber-900'>
              <strong className='font-semibold'>Important:</strong> You must be authorized
              to file on behalf of this company at Companies House. If you don&apos;t have
              the necessary permissions, the authorization will fail.
            </p>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col gap-3'>
            <button
              onClick={handleAuthorize}
              disabled={isRequestingAuth || !authUrl}
              className='w-full inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-6 py-3 text-base font-semibold text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              {isRequestingAuth ? (
                <>
                  <Loader2 className='h-5 w-5 animate-spin' />
                  <span>Preparing authorization...</span>
                </>
              ) : (
                <>
                  <Building2 className='h-5 w-5' />
                  <span>Authorize with Companies House</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isRequestingAuth}
              className='w-full rounded-full border border-gray-300 px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
